import React, { useEffect, useState } from "react";
import JobListing from "./JobListing";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setItems,
  setTotalItems,
  setIsTableLoading,
} from "src/redux/slices/JobBoardSlice";
import AddJobFormWrapper from "../Add/AddJobFormWrapper";
import EditJobFormWrapper from "../Edit/EditJobFormWrapper";
import {
  useDeleteJobMutation,
  useGetAllJobsWithPaginationQuery,
} from "src/services/JobBoardServices";

const JobListingWrapper = () => {
  const dispatch = useDispatch();

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [deleteJob] = useDeleteJobMutation();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.jobBoard);

  const { data, isLoading, isFetching } = useGetAllJobsWithPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "jobRole",
      "jobType",
      "workMode",
      "requirements",
      "responsibilities",
      "companyName",
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (jobId: string) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedId(jobId);
          setIsOpenEditForm(true);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.JOB_BOARD,
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold text-red-600">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Job ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteJob({ id: jobId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.JOB_BOARD,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "jobRole",
      headerName: "Job Role",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.jobRole} </div>
      ),
    },

    {
      field: "company",
      headerName: "company",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.companyName} </div>
      ),
    },
    {
      field: "JobType",
      headerName: "Job Type",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.jobType} </div>
      ),
    },
    {
      field: "WorkMode",
      headerName: "Job Mode",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.workMode} </div>
      ),
    },
    {
      field: "action",
      headerName: "",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  return (
    <div>
      <JobListing
        rows={items}
        columns={columns}
        getActionOptions={getActionOptions}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddFrom ? (
        <AddJobFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditJobFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          jobId={selectedId}
        />
      ) : null}
    </div>
  );
};

export default JobListingWrapper;
